"use client";

import { useQuery } from "@tanstack/react-query";
import { useTokenStore } from "@/lib/zustand";
import { getSolanaBalance } from "@/lib/solana";
import { Card, CardContent, CardFooter, CardHeader } from "../ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Input } from "../ui/input";
import { SelectTokenModal } from "../modals/select-token-modal";
import { Skeleton } from "../ui/skeleton";
import { CheckCircle2, XCircle } from "lucide-react";
import { addSeparators, isValidNumber, stripSeparators } from "@/lib/utils";
import CountUp from "react-countup";
import { useBalance } from "wagmi";
import { Address, formatUnits, zeroAddress } from "viem";
import { Button } from "../ui/button";
import { ChainId, ChainType } from "@lifi/sdk";
import { toast } from "sonner";
import { useSwapAccount } from "@/hooks/use-swap-account";
import { useState, useMemo, useEffect } from "react";
import { useTokenBalances } from "@/hooks/use-token-balances";
import { isNativeToken } from "@/constants/send-modal-helpers";

interface TokenSwapCardProps {
  title: string;
  amount: string;
  usdValue: number;
  onAmountChange: (value: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  type: "receive" | "send";
}

export const TokenSwapCard = ({
  title,
  amount,
  usdValue,
  onAmountChange,
  isLoading,
  disabled,
  type,
}: TokenSwapCardProps) => {
  const { toToken, fromToken, setFromTokenAmountUSD } = useTokenStore();
  const { toChain, fromChain, setSvmAddress, isValidSvmAddress } =
    useTokenStore();
  // Allow entering USD instead of token amount (only for send side)
  const [amountMode, setAmountMode] = useState<"token" | "usd">("usd");
  const [usdInput, setUsdInput] = useState<string>("");
  // caipAddress "solana:address" or "eip155:address"
  const GAS_RESERVES: Record<number, number> = {
    1: 0.003, // Ethereum
    10: 0.001, // Optimism
    56: 0.005, // BNB Chain — increased, cross-chain bridges cost more
    137: 0.5, // Polygon
    8453: 0.001, // Base
    42161: 0.001, // Arbitrum
    43114: 0.01, // Avalanche
  };
  const EVM_GAS_RESERVE = GAS_RESERVES[fromChain.id] || 0.002;
  const MIN_SOL_FEE = 0.01;

  // Map chainId to DexScreener chain slug
  const getDexScreenerChain = (chainId: number): string => {
    const chainMap: Record<number, string> = {
      [ChainId.SOL]: "solana",
      1: "ethereum",
      56: "bsc",
      137: "polygon",
      8453: "base",
      42161: "arbitrum",
      10: "optimism",
      43114: "avalanche",
    };
    return chainMap[chainId] || "ethereum";
  };

  // Native token addresses don't have charts - use wrapped versions
  const getNativeWrappedAddress = (chainId: number, tokenAddress: string): string => {
    // SOL native
    if (chainId === ChainId.SOL && tokenAddress === "11111111111111111111111111111111") {
      return "So11111111111111111111111111111111111111112";
    }
    // ETH native (zero address or 0x0...0)
    if ((chainId === 1 || chainId === 8453 || chainId === 42161 || chainId === 10) &&
        (tokenAddress === zeroAddress || tokenAddress === "0x0000000000000000000000000000000000000000")) {
      return "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2"; // WETH
    }
    // BNB native
    if (chainId === 56 && (tokenAddress === zeroAddress || tokenAddress === "0x0000000000000000000000000000000000000000")) {
      return "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c"; // WBNB
    }
    return tokenAddress;
  };

  const openDexScreener = (token: typeof fromToken) => {
    const chain = getDexScreenerChain(token.chainId);
    const address = getNativeWrappedAddress(token.chainId, token.address);
    window.open(`https://dexscreener.com/${chain}/${address}`, "_blank");
  };

  const { address, caipAddress } = useSwapAccount();
  const { data: userEvmBalance, isLoading: isLoadingEvmBalance } = useBalance({
    address: address as Address,
    token:
      type === "receive"
        ? toToken.address !== zeroAddress
          ? (toToken.address as Address)
          : undefined
        : fromToken.address !== zeroAddress
          ? (fromToken.address as Address)
          : undefined,
    chainId: type === "receive" ? toChain.id : fromChain.id,
    query: {
      select: (data) => data.value,
    },
  });

  const { availableTokens } = useTokenBalances();
  const fallbackFromTokenBalance = useMemo(() => {
    if (type !== "send") return undefined;
    const matched = availableTokens.find(
      (token) =>
        token.address === fromToken.address && token.network === fromChain.name,
    );
    return matched ? Number(matched.balance) : undefined;
  }, [availableTokens, fromChain.name, fromToken.address, type]);

  // Get fresh price from portfolio data (same source as portfolio USD display)
  // This ensures max button shows same USD value as portfolio
  const portfolioTokenPrice = useMemo(() => {
    const token = type === "send" ? fromToken : toToken;
    const chain = type === "send" ? fromChain : toChain;

    // Try exact match first (address + network)
    let matched = availableTokens.find(
      (t) => t.address?.toLowerCase() === token.address?.toLowerCase() && t.network === chain.name,
    );

    // Fallback: try address-only match for Solana tokens (network name might differ)
    if (!matched && chain.chainType === ChainType.SVM) {
      matched = availableTokens.find(
        (t) => t.address?.toLowerCase() === token.address?.toLowerCase() && t.network === "Solana",
      );
    }

    // Fallback: match native tokens by symbol (handles address format mismatch between LiFi and Para)
    // LiFi uses "11111111111111111111111111111111" for native SOL, Para uses ""
    if (!matched && isNativeToken(token.symbol)) {
      matched = availableTokens.find(
        (t) => t.symbol === token.symbol && t.isNative && t.network === chain.name,
      );
      // Also try "Solana" network name for SVM chains
      if (!matched && chain.chainType === ChainType.SVM) {
        matched = availableTokens.find(
          (t) => t.symbol === token.symbol && t.isNative && t.network === "Solana",
        );
      }
    }

    // Debug logging
    if (process.env.NODE_ENV !== "production") {
      console.log("[Price Debug]", {
        tokenSymbol: token.symbol,
        tokenAddress: token.address,
        chainName: chain.name,
        chainType: chain.chainType,
        matchedSymbol: matched?.symbol,
        matchedNetwork: matched?.network,
        matchedPrice: matched?.price,
        matchedBalance: matched?.balance,
        matchedBalanceUSD: matched?.balanceUSD,
      });
    }

    return matched?.price ?? 0;
  }, [availableTokens, fromToken, toToken, fromChain, toChain, type]);

  const { data: solanaBalance, isLoading: isLoadingSolanaBalance, status: solanaBalanceStatus } = useQuery({
    queryKey: ["solanaBalance", fromToken.address],
    queryFn: () =>
      getSolanaBalance(
        address as string,
        fromToken.address,
        fromToken.decimals,
      ),
    enabled: !!address && fromToken.chainId === ChainId.SOL,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: false,
  });

  const solanaTokenBalance = useMemo(() => {
    const isNativeSol = fromToken.address === "11111111111111111111111111111111";

    // Find portfolio match for comparison
    const portfolioMatch = availableTokens.find(
      (t) => t.address?.toLowerCase() === fromToken.address?.toLowerCase(),
    );

    let finalBalance: number | null = null;

    // For native SOL, use direct query (LAMPORTS_PER_SOL is always correct)
    if (isNativeSol) {
      finalBalance = solanaBalance ?? null;
    }
    // For SPL tokens: prefer direct query when it has successfully loaded
    // Direct query is authoritative after swaps (doesn't depend on Para's cached backend)
    // Portfolio data can be stale for several seconds after a swap
    else if (fromChain.chainType === ChainType.SVM && fromToken.address) {
      // Prefer direct query when it has successfully loaded (even if 0)
      // This ensures swap form shows correct balance immediately after swaps
      if (solanaBalanceStatus === "success" && solanaBalance !== undefined) {
        finalBalance = solanaBalance;
      }
      // Fall back to portfolio only if direct query hasn't loaded yet
      else if (portfolioMatch && Number(portfolioMatch.balance) >= 0) {
        finalBalance = Number(portfolioMatch.balance);
      }
    }

    return finalBalance;
  }, [solanaBalance, solanaBalanceStatus, availableTokens, fromChain.chainType, fromToken.address]);

  const onMaxClick = () => {
    if (process.env.NODE_ENV !== "production") {
      console.log("[Max Button] Clicked", {
        chainType: fromChain.chainType,
        fromTokenSymbol: fromToken.symbol,
        fromTokenAddress: fromToken.address,
        fromTokenDecimals: fromToken.decimals,
        solanaTokenBalance,
        userEvmBalance: userEvmBalance?.toString(),
      });
    }

    if (
      fromChain.chainType === ChainType.EVM &&
      (fromToken.address === zeroAddress ||
        fromToken.address === "0x0000000000000000000000000000000000000000")
    ) {
      const rawBalance = userEvmBalance
        ? Number(formatUnits(userEvmBalance, fromToken?.decimals ?? 18))
        : 0;
      const maxAmount = Math.max(0, rawBalance - EVM_GAS_RESERVE);

      if (maxAmount <= 0) {
        toast.info("Insufficient balance for EVM gas fees");
      }

      onAmountChange(maxAmount.toFixed(6));
      return;
    }

    if (fromChain.chainType === ChainType.SVM) {
      const maxAmount = Math.max(0, (solanaTokenBalance || 0) - MIN_SOL_FEE);

      if (process.env.NODE_ENV !== "production") {
        console.log("[Max Button SVM]", {
          solanaTokenBalance,
          maxAmount,
          maxAmountFixed: maxAmount.toFixed(6),
          fromTokenSymbol: fromToken.symbol,
          fromTokenDecimals: fromToken.decimals,
        });
      }

      if (
        fromToken.address === "11111111111111111111111111111111" &&
        maxAmount <= 0
      ) {
        toast.info("Insufficient balance for Solana transaction fees");
      }

      onAmountChange(maxAmount.toFixed(6));
      return;
    }

    onAmountChange(
      formatUnits(userEvmBalance || BigInt(0), fromToken?.decimals ?? 18),
    );
  };

  // TODO: switch chains if fromToken chain is different from connected chain

  const activeToken = useMemo(
    () => (type === "receive" ? toToken : fromToken),
    [type, toToken, fromToken],
  );
  // Update usdInput when amount changes for receive side
  useEffect(() => {
    // Skip mode changes when there's no meaningful amount to convert.
    // This prevents render thrashing during token selection (e.g., selecting KENJI
    // from featured dropdown resets amount to "0" and portfolioTokenPrice may be 0
    // if the token isn't in the user's portfolio yet).
    const hasAmount = amount && Number(amount) > 0;

    if (
      type === "receive" &&
      amountMode === "usd" &&
      portfolioTokenPrice > 0 &&
      hasAmount
    ) {
      const usdValue = (Number(amount) * portfolioTokenPrice).toFixed(2);
      setUsdInput(usdValue);
    }

    // Reset to token mode when price is unavailable, but only if user has entered an amount.
    // No point flipping modes when amount is 0 - it just causes unnecessary re-renders.
    if (portfolioTokenPrice === 0 && hasAmount) {
      setUsdInput("");
      setAmountMode("token");
    }
  }, [amount, portfolioTokenPrice, type, amountMode]);

  // Local usdInput must be cleared when amount is reset externally (e.g., dropdown selection).
  // The hasAmount guard in the effect above intentionally skips that branch, so this dedicated
  // effect handles it.
  useEffect(() => {
    if (amount === "0" || amount === "") {
      setUsdInput("");
    }
  }, [amount]);

  return (
    <Card className="border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.025)] rounded-[14px] shadow-none transition-all duration-200 ease-out hover:bg-[rgba(255,255,255,0.06)]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-[0.5px] text-[#555]">
            {title}
          </span>
          <Avatar className="h-8 w-8 rounded">
            <AvatarImage
              src={type === "receive" ? toChain.logoURI : fromChain.logoURI}
              alt={type === "receive" ? toChain.name : fromChain.name}
            />
            <AvatarFallback className="bg-[#2D2E33]">
              {type === "receive"
                ? toChain?.name?.slice(0, 2)?.toUpperCase() || ""
                : fromChain?.name?.slice(0, 2)?.toUpperCase() || ""}
            </AvatarFallback>
          </Avatar>
        </div>
        <SelectTokenModal type={type} />
      </CardHeader>
      <CardContent>
        {/* Amount and Token Selection */}
        <div className="flex items-center justify-between gap-4">
          <Input
            type="text"
            value={
              amountMode === "usd" && type === "send"
                ? addSeparators(usdInput)
                : addSeparators(amount)
            }
            onChange={(e) => {
              const val = e.target.value;
              const rawNumber = stripSeparators(val);

              if (!isValidNumber(rawNumber)) {
                if (amountMode === "token") {
                  onAmountChange("");
                } else {
                  onAmountChange("");
                  setUsdInput("");
                }
                return;
              }

              if (amountMode === "token") {
                onAmountChange(rawNumber);
              } else {
                // If portfolioTokenPrice is 0, auto-switch to token mode rather than failing silently
                if (portfolioTokenPrice <= 0) {
                  setAmountMode("token");
                  setUsdInput("");
                  onAmountChange(rawNumber);
                  return;
                }
                setUsdInput(rawNumber);

                // Set USD amount immediately for credit card flow - don't wait for LiFi quote
                // (quote may fail for over-balance amounts, but credit card doesn't use wallet balance)
                if (type === "send") {
                  setFromTokenAmountUSD(rawNumber);
                }

                // Use BigInt for precise USD→token conversion (avoids float precision loss)
                // Scale factor: 10^18 for 18 decimal precision
                const SCALE = 10n ** 18n;

                // Convert USD and price to scaled BigInt
                // Multiply by 1e18 first to preserve decimal precision before BigInt conversion
                const usdScaled = BigInt(Math.round(parseFloat(rawNumber) * 1e18));
                const priceScaled = BigInt(Math.round(portfolioTokenPrice * 1e18));

                // tokenAmount = (usd * SCALE) / price
                // Result is scaled by SCALE (has 18 implicit decimal places)
                const tokenAmtScaled = (usdScaled * SCALE) / priceScaled;

                // Convert BigInt to decimal string with 18 decimal places
                const tokenAmtStr = tokenAmtScaled.toString().padStart(19, '0');
                const wholePart = tokenAmtStr.slice(0, -18) || '0';
                const decimalPart = tokenAmtStr.slice(-18).replace(/0+$/, '');
                const tokenAmt = decimalPart ? `${wholePart}.${decimalPart}` : wholePart;

                onAmountChange(tokenAmt);
              }
            }}
            className="w-full pl-0 overflow-y-hidden text-[#444] border-none bg-transparent text-[28px] font-light focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-default"
            placeholder={
              amountMode === "usd" && type === "send" ? "$0.00" : "0.00"
            }
            disabled={disabled || isLoading}
          />
        </div>
      </CardContent>
      <CardFooter className="flex flex-col gap-4">
        <div className="w-full flex flex-wrap items-center justify-between gap-1">
          {isLoading ? (
            <Skeleton className="h-5 w-12 border-none rounded-md" />
          ) : (
            <div className="text-[11px] uppercase tracking-[0.5px] text-[#555]">
              {amountMode === "usd" && type === "send" ? (
                <span className="text-[#a0a0a0]">
                  ${addSeparators(usdInput || "0.00")} USD
                </span>
              ) : type === "send" ? (
                // SEND side: Use portfolio price (what you own)
                portfolioTokenPrice > 0 ? (
                  <CountUp
                    end={Number(amount || 0) * portfolioTokenPrice}
                    className="text-[#a0a0a0]"
                    prefix="$"
                    suffix=" USD"
                    decimals={2}
                  />
                ) : (
                  <span className="text-[#666]" title="Price data loading...">
                    — USD
                  </span>
                )
              ) : (
                // RECEIVE side: Use LiFi quote (what you'll get from swap)
                usdValue > 0 ? (
                  <CountUp
                    end={usdValue}
                    className="text-[#a0a0a0]"
                    prefix="$"
                    suffix=" USD"
                    decimals={2}
                  />
                ) : (
                  <span className="text-[#666]" title="Fetching quote...">
                    — USD
                  </span>
                )
              )}
            </div>
          )}
          {type === "send" && (
            <div className="flex flex-wrap items-center justify-end gap-1">
              <div className="flex items-center gap-1 mr-1">
                <Button
                  type="button"
                  variant={amountMode === "token" ? "secondary" : "ghost"}
                  size="sm"
                  className="h-6 px-2 text-[11px] text-[#f9cd4f] border border-[rgba(249,205,79,0.2)] bg-[rgba(249,205,79,0.05)] rounded-[4px]"
                  onClick={() => setAmountMode("token")}
                  disabled={disabled || isLoading}
                >
                  {activeToken?.symbol || "TOKEN"}
                </Button>
                {portfolioTokenPrice > 0 && (
                  <Button
                    type="button"
                    variant={amountMode === "usd" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-6 px-2 text-[11px] text-[#f9cd4f] border border-[rgba(249,205,79,0.2)] bg-[rgba(249,205,79,0.05)] rounded-[4px]"
                    onClick={() => {
                      setAmountMode("usd");
                      setUsdInput(
                        portfolioTokenPrice > 0
                          ? addSeparators(Number(amount || 0) * portfolioTokenPrice)
                          : "",
                      );
                    }}
                    disabled={disabled || isLoading}
                  >
                    USD
                  </Button>
                )}
              </div>
              <div className="text-xs sm:text-sm text-foreground">
                {isLoadingEvmBalance || isLoadingSolanaBalance ? (
                  <Skeleton className="h-5 w-10 border-none rounded-md" />
                ) : (
                  <span className="text-[11px] uppercase tracking-[0.5px] text-[#555]">
                    Balance: {/* connected? */}
                    {caipAddress
                      ? // on evm?
                        caipAddress.includes("eip155")
                        ? // got a balance?
                          userEvmBalance
                          ? // get the balance
                            parseFloat(
                              formatUnits(
                                userEvmBalance,
                                fromToken?.decimals || 18,
                              ),
                            ).toLocaleString()
                          : fallbackFromTokenBalance !== undefined
                            ? fallbackFromTokenBalance.toLocaleString()
                            : // fall back to 0 if not
                              "0.00"
                        : // on solana?
                          // Use solanaTokenBalance (our computed value that prefers fresh direct query)
                          // Don't fall back to stale portfolio data - show 0.00 if not ready
                          typeof solanaTokenBalance === "number"
                            ? `${solanaTokenBalance.toFixed(4)}`
                            : "0.00"
                      : "0.00"}
                  </span>
                )}
              </div>
              <Button
                variant="secondary"
                type="button"
                size="sm"
                className="h-6 px-[8px] py-[2px] text-[11px] text-[#f9cd4f] border border-[rgba(249,205,79,0.2)] bg-[rgba(249,205,79,0.05)] rounded-[4px]"
                onClick={() => {
                  if (amountMode === "token") {
                    onMaxClick();
                  } else {
                    // compute max USD value based on max token amount
                    if (caipAddress?.includes("eip155")) {
                      const currentBalance = Number(
                        formatUnits(
                          userEvmBalance || BigInt("0"),
                          fromToken?.decimals || 18,
                        ),
                      );
                      const isNative = fromToken?.address === zeroAddress;

                      if (isNative) {
                        const gasReserve = GAS_RESERVES[fromChain.id] || 0.002;
                        if (currentBalance > gasReserve) {
                          const maxToken = currentBalance - gasReserve;
                          setUsdInput(
                            portfolioTokenPrice > 0
                              ? (maxToken * portfolioTokenPrice).toFixed(2)
                              : "0",
                          );
                          onAmountChange(maxToken.toString());
                        } else {
                          toast.info("Insufficient balance for EVM gas fees");
                          setUsdInput("0");
                          onAmountChange("0");
                        }
                      } else {
                        setUsdInput(
                          portfolioTokenPrice > 0
                            ? (currentBalance * portfolioTokenPrice).toFixed(2)
                            : "0",
                        );
                        onAmountChange(currentBalance.toString());
                      }
                    } else {
                      const currentBalance = Number(solanaTokenBalance || 0);
                      const isNativeSol =
                        fromToken?.address ===
                        "11111111111111111111111111111111";

                      if (isNativeSol) {
                        if (currentBalance > MIN_SOL_FEE) {
                          const maxToken = currentBalance - MIN_SOL_FEE;
                          setUsdInput(
                            portfolioTokenPrice > 0
                              ? (maxToken * portfolioTokenPrice).toFixed(2)
                              : "0",
                          );
                          onAmountChange(maxToken.toString());
                        } else {
                          toast.info(
                            "Insufficient balance for Solana transaction fees",
                          );
                          setUsdInput("0");
                          onAmountChange("0");
                        }
                      } else {
                        // SPL token (non-native Solana)
                        const calculatedUSD = currentBalance * portfolioTokenPrice;
                        if (process.env.NODE_ENV !== "production") {
                          console.log("[Max Button SPL]", {
                            currentBalance,
                            portfolioTokenPrice,
                            calculatedUSD,
                            usdString: calculatedUSD.toFixed(2),
                            fromTokenSymbol: fromToken?.symbol,
                          });
                        }
                        setUsdInput(
                          portfolioTokenPrice > 0
                            ? calculatedUSD.toFixed(2)
                            : "0",
                        );
                        onAmountChange(currentBalance.toString());
                      }
                    }
                  }
                }}
                disabled={disabled || isLoading}
              >
                Max
              </Button>
              <Button
                variant="secondary"
                type="button"
                size="sm"
                className="h-6 px-[8px] py-[2px] text-[11px] text-[#f9cd4f] border border-[rgba(249,205,79,0.2)] bg-[rgba(249,205,79,0.05)] rounded-[4px]"
                onClick={() => openDexScreener(fromToken)}
                title="View chart on DexScreener"
              >
                Chart
              </Button>
            </div>
          )}
          {type === "receive" && (
            <div className="w-full flex justify-end">
              <Button
                variant="secondary"
                type="button"
                size="sm"
                className="h-6 px-[8px] py-[2px] text-[11px] text-[#f9cd4f] border border-[rgba(249,205,79,0.2)] bg-[rgba(249,205,79,0.05)] rounded-[4px]"
                onClick={() => openDexScreener(toToken)}
                title="View chart on DexScreener"
              >
                Chart
              </Button>
            </div>
          )}
        </div>
        {(type === "receive" && toChain.chainType === "SVM") ||
        (type === "send" && fromChain.chainType === "SVM") ? (
          <div className="w-full relative">
            {isValidSvmAddress !== null && (
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 z-[1] flex items-center justify-center">
                {isValidSvmAddress ? (
                  <CheckCircle2 className="w-5 h-5 text-[#f9cd4f]" />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setSvmAddress("");
                    }}
                    className="hover:opacity-80 transition-opacity flex items-center justify-center p-0 m-0"
                    aria-label="Clear Input Button"
                  >
                    <XCircle
                      aria-label="Clear Input Icon"
                      className="w-5 h-5 text-[#EC3939]"
                    />
                  </button>
                )}
              </div>
            )}
          </div>
        ) : null}
      </CardFooter>
    </Card>
  );
};
